import Toggle from "@/app/components/ui/Toggles";
import Input from "@/app/components/ui/Inputs/Input";
import NumberInput from "@/app/components/ui/Inputs/NumberInput";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useAddTileAPI } from "@/app/hooks/api_hooks/Tiles/useAddTileAPI";
import { useTilesListAPI } from "@/app/hooks/api_hooks/Tiles/useTilesListAPI";
import { useTilesConsultationAPI } from "@/app/hooks/api_hooks/Tiles/useTilesConsultationAPI";
import { ITilesOnboardBody } from "@/api_framework/api_modals/Tiles";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { TbPlus, TbTrash } from "react-icons/tb";

const TILE_TYPE_OPTIONS: ISelectMenuItemData[] = [
  { title: "Service", id: "service" },
  { title: "Collection", id: "collection" },
];

export interface IAddTilePersonaRow {
  value: string;
  sorting_key: number | "";
}

export interface IAddTileRibbonPersonaRow {
  value: string;
}

export interface IAddTileRibbonRow {
  chip_txt: string;
  persona: IAddTileRibbonPersonaRow[];
}

export interface IAddTileFormFields {
  tile_name: string;
  collection_id: string;
  type: string;
  visible: boolean;
  persona: IAddTilePersonaRow[];
  ribbon: IAddTileRibbonRow[];
}

const RibbonRowFields: React.FC<{
  formHook: ReturnType<typeof useForm<IAddTileFormFields>>;
  rowIndex: number;
  onRemoveRow: () => void;
  disableRemoveRow: boolean;
}> = ({ formHook, rowIndex, onRemoveRow, disableRemoveRow }) => {
  const personaArray = useFieldArray({ name: `ribbon.${rowIndex}.persona` as const, control: formHook.control });

  return (
    <div className="border border-gray-200 rounded-md p-3 mb-2.5">
      <label className="text-xs font-medium text-gray-600 mb-1 block">
        Chip Text<span className="text-red-600 ml-0.5">*</span>
      </label>
      <div className="grid grid-cols-[1fr_28px] gap-2.5 items-center mb-2.5">
        <Input
          placeholder="e.g. 50% Off"
          register={formHook.register(`ribbon.${rowIndex}.chip_txt` as const, { required: true })}
          fieldError={formHook.formState.errors.ribbon?.[rowIndex]?.chip_txt}
          errorMessages={[{ type: "required", message: "Chip text is required" }]}
        />
        {!disableRemoveRow && (
          <button
            type="button"
            className="w-7 h-[30px] flex items-center justify-center rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
            onClick={onRemoveRow}
            title="Delete ribbon"
          >
            <TbTrash className="w-4 h-4" />
          </button>
        )}
      </div>
      <p className="text-xs font-medium text-gray-600 mb-1.5 mt-4">Persona</p>
      {personaArray.fields.map((field, personaIndex) => (
        <div key={field.id} className="mb-2">
          <Input
            placeholder="e.g. Dog Owners"
            register={formHook.register(`ribbon.${rowIndex}.persona.${personaIndex}.value` as const, {
              required: true,
            })}
            fieldError={formHook.formState.errors.ribbon?.[rowIndex]?.persona?.[personaIndex]?.value}
            errorMessages={[{ type: "required", message: "Persona is required" }]}
          />
        </div>
      ))}
      <button
        type="button"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 mt-4"
        onClick={() => personaArray.append({ value: "" })}
      >
        <span className="w-[18px] h-[18px] rounded-full bg-indigo-50 flex items-center justify-center">
          <TbPlus className="w-3 h-3" />
        </span>
        Add persona
      </button>
    </div>
  );
};

export const AddTileModalContent: React.FC<{
  formHook: ReturnType<typeof useForm<IAddTileFormFields>>;
  hideIdentityFields?: boolean;
}> = ({ formHook, hideIdentityFields = false }) => {
  const personaArray = useFieldArray({ name: "persona", control: formHook.control });
  const ribbonArray = useFieldArray({ name: "ribbon", control: formHook.control });
  const fetchConsultationOptions = useTilesConsultationAPI();
  const { mode } = useModalState("TILE_ADD_MODAL") as { mode?: "live" | "draft" };
  const isVisible = formHook.watch("visible");
  const selectedType = formHook.watch("type");

  const [consultationOptions, setConsultationOptions] = useState<ISelectMenuItemData[]>([]);

  useEffect(() => {
    if (hideIdentityFields) return;
    formHook.setValue("collection_id", "");
    if (!selectedType) {
      setConsultationOptions([]);
      return;
    }
    fetchConsultationOptions(selectedType, mode ?? "live").then((res) => {
      setConsultationOptions(res ? res.map((item) => ({ title: item.label, id: item.value })) : []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, mode, hideIdentityFields]);

  return (
    <form className="mt-2">
      <div className="mb-5">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-600 mb-2.5">Tile Details</p>
        {!hideIdentityFields && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                Tile Name<span className="text-red-600 ml-0.5">*</span>
              </label>
              <Input
                placeholder="e.g. Consultation"
                register={formHook.register("tile_name", { required: true })}
                fieldError={formHook.formState.errors.tile_name}
                errorMessages={[{ type: "required", message: "Tile name is required" }]}
              />
            </div>
            <div className="mt-2">
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                Type<span className="text-red-600 ml-0.5">*</span>
              </label>
              <NonSearchableSelectMenu
                selectItems={TILE_TYPE_OPTIONS}
                placeholder="Select Type"
                register={formHook.register("type", { required: true })}
                fieldError={formHook.formState.errors.type}
                errorMessages={[{ type: "required", message: "Type is required" }]}
                onSelectItem={(item) => {
                  if (item) formHook.setValue("type", item.id);
                }}
                showTooltips={false}
                showTypedErrors={true}
                showDropdownIcon={true}
              />
            </div>
            <div className="mt-2">
              {selectedType === "collection" && (
                <>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    Collection ID<span className="text-red-600 ml-0.5">*</span>
                  </label>
                  <NonSearchableSelectMenu
                    key="collection_id"
                    selectItems={consultationOptions}
                    placeholder="Select Collection"
                    register={formHook.register("collection_id", { required: true })}
                    fieldError={formHook.formState.errors.collection_id}
                    errorMessages={[{ type: "required", message: "Collection ID is required" }]}
                    onSelectItem={(item) => {
                      formHook.setValue("collection_id", item ? item.id : "");
                    }}
                    showTooltips={false}
                    showTypedErrors={true}
                    showDropdownIcon={true}
                  />
                </>
              )}
              {selectedType === "service" && (
                <>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    Match Key<span className="text-red-600 ml-0.5">*</span>
                  </label>
                  <NonSearchableSelectMenu
                    key="match_key"
                    selectItems={consultationOptions}
                    placeholder="Select Match Key"
                    register={formHook.register("collection_id", { required: true })}
                    fieldError={formHook.formState.errors.collection_id}
                    errorMessages={[{ type: "required", message: "Match Key is required" }]}
                    onSelectItem={(item) => {
                      formHook.setValue("collection_id", item ? item.id : "");
                    }}
                    showTooltips={false}
                    showTypedErrors={true}
                    showDropdownIcon={true}
                  />
                </>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 mt-3">
          <div>
            <p className="text-sm font-medium text-gray-900">Visible</p>
            <p className="text-xs text-gray-500">Show this tile on the home screen immediately.</p>
          </div>
          <Toggle checked={isVisible} onToggle={(state) => formHook.setValue("visible", state)} />
        </div>
      </div>

      <div className="mb-5">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-600 mb-2.5">Persona</p>
        {personaArray.fields.length > 0 && (
          <div className="grid grid-cols-[1fr_100px_28px] gap-2.5 mb-1">
            <p className="text-xs font-medium text-gray-600">Persona</p>
            <p className="text-xs font-medium text-gray-600">Sorting key</p>
            <span />
          </div>
        )}
        {personaArray.fields.map((field, index) => (
          <div key={field.id} className="grid grid-cols-[1fr_100px_28px] gap-2.5 items-center mb-2">
            <Input
              placeholder="e.g. Dog Owners"
              register={formHook.register(`persona.${index}.value` as const, { required: true })}
              fieldError={formHook.formState.errors.persona?.[index]?.value}
              errorMessages={[{ type: "required", message: "Persona is required" }]}
            />
            <NumberInput
              placeholder="Sorting key"
              register={formHook.register(`persona.${index}.sorting_key` as const, { required: true })}
              fieldError={formHook.formState.errors.persona?.[index]?.sorting_key}
              errorMessages={[{ type: "required", message: "Sorting key is required" }]}
            />
            {personaArray.fields.length > 1 && (
              <button
                type="button"
                className="w-7 h-[30px] flex items-center justify-center rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
                onClick={() => personaArray.remove(index)}
              >
                <TbTrash className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 mt-3"
          onClick={() => personaArray.append({ value: "", sorting_key: "" })}
        >
          <span className="w-[18px] h-[18px] rounded-full bg-indigo-50 flex items-center justify-center">
            <TbPlus className="w-3 h-3" />
          </span>
          Add another
        </button>
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-gray-600 mb-2.5">Ribbon</p>
        {ribbonArray.fields.map((field, index) => (
          <RibbonRowFields
            key={field.id}
            formHook={formHook}
            rowIndex={index}
            onRemoveRow={() => ribbonArray.remove(index)}
            disableRemoveRow={ribbonArray.fields.length === 1}
          />
        ))}
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 mt-1"
          onClick={() => ribbonArray.append({ chip_txt: "", persona: [{ value: "" }] })}
        >
          <span className="w-[18px] h-[18px] rounded-full bg-indigo-50 flex items-center justify-center">
            <TbPlus className="w-3 h-3" />
          </span>
          Add another
        </button>
      </div>
    </form>
  );
};

const AddTileModal = () => {
  const { setInvisible } = useModalActions("TILE_ADD_MODAL");
  const { execute: addTile } = useAddTileAPI();
  const fetchTilesList = useTilesListAPI();
  const [isSaving, setIsSaving] = useState(false);

  const formHook = useForm<IAddTileFormFields>({
    mode: "onChange",
    defaultValues: {
      type: "",
      visible: true,
      persona: [{ value: "", sorting_key: "" }],
      ribbon: [{ chip_txt: "", persona: [{ value: "" }] }],
    },
  });

  const closeAndReset = () => {
    formHook.reset();
    setInvisible();
  };

  const buildPayload = (values: IAddTileFormFields): ITilesOnboardBody => {
    const personaRows = values.persona.filter((row) => row.value);
    return {
      tile_name: values.tile_name,
      ...(values.type === "service"
        ? { match_key: values.collection_id }
        : { collection_id: values.collection_id }),
      type: values.type,
      visible: values.visible,
      persona: personaRows.map((row) => row.value),
      sorting_key: personaRows.reduce<Record<string, number>>((acc, row) => {
        acc[row.value] = Number(row.sorting_key) || 0;
        return acc;
      }, {}),
      ribbon: values.ribbon.map((row) => ({
        chip_txt: row.chip_txt,
        persona: row.persona.map((personaRow) => personaRow.value).filter(Boolean),
      })),
    };
  };

  const onSave = () => {
    formHook.trigger().then((isValid) => {
      if (!isValid) return;
      setIsSaving(true);
      addTile(buildPayload(formHook.getValues()))
        .then((res) => {
          if (res?.status) {
            fetchTilesList("draft");
            closeAndReset();
          }
        })
        .finally(() => setIsSaving(false));
    });
  };

  return (
    <ModalPrompt
      modalId="TILE_ADD_MODAL"
      contentOverflow={false}
      isLoading={isSaving}
      dialogClassName="sm:my-8 sm:w-full sm:max-w-lg sm:p-6 max-h-[70vh] overflow-y-auto"
      button={{
        primaryButtonText: "Add Tile",
        primaryButtonClassname: "bg-[#1a3c87] hover:bg-[#1a3c87]",
        secondaryButtonText: "Cancel",
        primaryButtonDisabled: isSaving,
        primaryCallback: onSave,
        secondaryCallback: closeAndReset,
        shouldHideOnClick: false,
      }}
      title="Add Tile"
      content={<AddTileModalContent formHook={formHook} />}
    />
  );
};

export default AddTileModal;
